import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import { useOverlay } from '@flipster/overlay';
import { walletsBunja } from '@flipster/r230522/original/wallet';
import { Modal } from '@flipster/react/design-system/modal';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { PusdtCurrency, UsdtCurrency } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import { CurrencyDisplay } from '../../misc/display';
import { earnSummaryBunja, getEarnSummaryOfCurrency, usdBalanceEmptyThreshold } from '../../shared/earn/state';
import { useDepositModal } from '../../shared/wallet-modal/deposit';
import { ManagePusdtModal } from './manage-pusdt-modal';

interface AccessPremiumEarnModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

export const AccessPremiumEarnModal = ({ isOpen, onClose, onExit }: AccessPremiumEarnModalProps) => {
  const openDepositModal = useDepositModal();
  const overlay = useOverlay();
  const { walletsAtom } = useBunja(walletsBunja);
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);

  const wallets = useAtomValue(walletsAtom);
  const earnSummary = useAtomValue(earnSummaryAtom);

  const usdtWalletBalance = Num(wallets?.[UsdtCurrency]?.walletBalanceExclWithdrawalPending ?? 0);
  const pusdtBaseAprPct = getEarnSummaryOfCurrency(PusdtCurrency, earnSummary).baseAprPct;

  return (
    <Modal.Root isOpen={isOpen} onClose={onClose} onExit={onExit}>
      <Modal.Header>
        <Modal.Title label={<FormattedMessage id='Earn.r260220.Pusdt_deposit_usdt_to_access_premium_earn_usdt' />} />
      </Modal.Header>
      <Modal.Content>
        <div className={stack({ gap: 16 })}>
          <div
            className={stack({
              p: 16,
              gap: 16,
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'border.default.level-1',
              borderRadius: 8,
            })}
          >
            <div className={hstack({ gap: 16, color: 'content.default.level-5' })}>
              <CurrencyDisplay
                currency={UsdtCurrency}
                icon={<CurrencyDisplay.Icon size={32} space={16} />}
                mainLabel={<CurrencyDisplay.Label value='shortName' css={{ textStyle: 'Text/L/Strong' }} />}
              />
              <Formatted.Number
                value={usdtWalletBalance}
                toFixed
                precision={8}
                css={{ flex: 1, textAlign: 'right', textStyle: 'Text/L/Monospace/Strong' }}
                suffix={UsdtCurrency}
              />
            </div>
            <div
              className={css({
                pt: 8,
                textStyle: 'Text/S/Regular',
                borderTopWidth: '1px',
                borderTopStyle: 'solid',
                borderColor: 'border.default.level-1',
                color: 'content.default.level-1',
              })}
            >
              <FormattedMessage
                id='Earn.r260220.Pusdt_amount_usdt_minimum_is_required_for_premium_earn_usdt'
                values={{
                  amount: usdBalanceEmptyThreshold,
                }}
              />
            </div>
          </div>
          <div
            className={css({
              textStyle: 'Text/L/Regular',
              color: 'content.default.level-3',
            })}
          >
            <FormattedMessage
              id='Earn.r260220.Pusdt_deposit_usdt_and_subscribe_to_premium_earn_usdt_to_start_earning_pct_base_apr'
              values={{
                pct: pusdtBaseAprPct.toString(),
              }}
            />
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.Button
          hierarchy='secondary'
          color='neutral'
          size='large'
          label={<FormattedMessage id='Common.r251008.Not_now' />}
          onClick={() => {
            onClose();
            overlay.open(({ isOpen, onClose, unmount }) => (
              <ManagePusdtModal isOpen={isOpen} onClose={onClose} onExit={unmount} />
            ));
          }}
        />
        <Modal.Button
          size='large'
          label={<FormattedMessage id='Autogen_common.r251126.s_Deposit_now' />}
          onClick={() => {
            onClose();
            openDepositModal({ defaultValues: { currency: UsdtCurrency } });
          }}
        />
      </Modal.Footer>
    </Modal.Root>
  );
};
